import { BaseLLMAdapter, validateLLMResponse } from "./baseAdapter";
import { getMaxOutputTokens, needsAnthropicContextBetaHeader, ANTHROPIC_1M_BETA_HEADER } from "@carousel-office/shared";
// ═══════════════════════════════════════════════════════════════════════════
// TIMEOUT CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════
/** Time to wait for first token in streaming requests (60s) */
const FIRST_TOKEN_TIMEOUT_MS = 60000;
/** Time to wait between chunks during streaming - heartbeat (30s) */
const CHUNK_HEARTBEAT_TIMEOUT_MS = 30000;
/** Longer heartbeat for tool calls - LLM can pause 4m+ while generating big params */
const TOOL_CALL_HEARTBEAT_TIMEOUT_MS = 240000;
/** Timeout for non-streaming complete() requests (5 minutes) */
const NON_STREAMING_TIMEOUT_MS = 300000;
/**
 * Keywords AI adapter implementation
 * Includes automatic background key refresh to prevent 403 errors
 */
export class KeywordsAdapter extends BaseLLMAdapter {
    constructor(authManager, jwtToken, timeout) {
        super();
        this.name = "keywords";
        this.REFRESH_INTERVAL_MS = 15000; // Check every 15 seconds like working Python implementation
        this.authManager = authManager;
        this.jwtToken = jwtToken;
        this.timeout = timeout || 180000; // 3 minutes to support extended thinking
        this.baseUrl = "https://api.keywordsai.co/api";
        // Start refresh loop if we have a token
        if (jwtToken) {
            this.startKeyRefreshLoop();
        }
    }
    /**
     * Start background refresh loop (like the working Python implementation)
     * Polls every 15 seconds to ensure key is always fresh
     */
    startKeyRefreshLoop() {
        if (this.refreshInterval) {
            return; // Already running
        }
        console.log('[KeywordsAdapter] Starting keywords key refresh loop');
        this.refreshInterval = setInterval(async () => {
            try {
                if (this.jwtToken) {
                    // This will check expiry and refresh if needed
                    await this.ensureApiKey();
                }
            }
            catch (error) {
                console.error('[KeywordsAdapter] Error in keywords refresh loop:', error);
            }
        }, this.REFRESH_INTERVAL_MS);
    }
    /**
     * Stop the refresh loop (call on cleanup/logout)
     */
    stopKeyRefreshLoop() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = undefined;
            console.log('[KeywordsAdapter] Stopped keywords key refresh loop');
        }
    }
    /**
     * Update JWT token (called when auth state changes)
     * Architecture: Integrates with AuthService token updates
     */
    setJwtToken(jwtToken) {
        // Clear cached API key when token changes
        if (this.jwtToken !== jwtToken) {
            this._apiKey = undefined;
        }
        this.jwtToken = jwtToken;
        // Start/stop refresh loop based on token
        if (jwtToken) {
            this.startKeyRefreshLoop();
        }
        else {
            this.stopKeyRefreshLoop();
        }
    }
    /**
     * Force refresh the API key (used after 403 errors)
     */
    async forceRefreshApiKey() {
        if (!this.jwtToken) {
            throw new Error("JWT token required for Keywords API");
        }
        console.log('[KeywordsAdapter] Force refreshing Keywords API key');
        this._apiKey = undefined;
        this._apiKey = await this.authManager.getTempKeywordsKey(this.jwtToken);
        return this._apiKey;
    }
    async ensureApiKey() {
        if (!this.jwtToken) {
            throw new Error("JWT token required for Keywords API");
        }
        if (!this._apiKey || this.authManager.isKeyExpired('keywords', this.jwtToken)) {
            console.log("[KeywordsAdapter] Fetching new Keywords API key");
            this._apiKey = await this.authManager.getTempKeywordsKey(this.jwtToken);
        }
        return this._apiKey;
    }
    getModelProvider(model) {
        const normalized = (model || "").toLowerCase();
        if (normalized.startsWith("anthropic/") || normalized.includes("claude")) {
            return "anthropic";
        }
        if (normalized.startsWith("gemini/") || normalized.includes("gemini")) {
            return "gemini";
        }
        if (normalized.startsWith("openai/") ||
            normalized.includes("gpt-") ||
            normalized.startsWith("o1") ||
            normalized.startsWith("o3") ||
            normalized.startsWith("o4")) {
            return "openai";
        }
        return "unknown";
    }
    mergeExtraBodyIntoRequest(requestData, extraBody) {
        if (!extraBody || typeof extraBody !== "object") {
            return;
        }
        // Respan/Keywords flattens extra_body at request root for OpenAI-compatible calls.
        Object.assign(requestData, extraBody);
    }
    applyProviderFineGrainedToolStreamingFlags(requestData, model) {
        if (!requestData.stream) {
            return;
        }
        if (!Array.isArray(requestData.tools) || requestData.tools.length === 0) {
            return;
        }
        const provider = this.getModelProvider(model);
        if (provider === "anthropic") {
            requestData.tools = requestData.tools.map((tool) => ({
                ...tool,
                eager_input_streaming: true,
            }));
            return;
        }
        if (provider === "gemini") {
            const existingToolConfig = requestData.toolConfig && typeof requestData.toolConfig === "object"
                ? requestData.toolConfig
                : {};
            const existingFunctionCallingConfig = existingToolConfig.functionCallingConfig &&
                typeof existingToolConfig.functionCallingConfig === "object"
                ? existingToolConfig.functionCallingConfig
                : {};
            requestData.toolConfig = {
                ...existingToolConfig,
                functionCallingConfig: {
                    mode: "AUTO",
                    ...existingFunctionCallingConfig,
                    streamFunctionCallArguments: true,
                },
            };
        }
    }
    bindExternalSignal(signal, controller) {
        if (!signal) {
            return () => { };
        }
        if (signal.aborted) {
            controller.abort();
            return () => { };
        }
        const onAbort = () => controller.abort();
        signal.addEventListener("abort", onAbort);
        return () => signal.removeEventListener("abort", onAbort);
    }
    /**
     * Cleanup resources
     */
    async close() {
        this.stopKeyRefreshLoop();
    }
    async complete(model, messages, options) {
        const apiKey = await this.ensureApiKey();
        const processedMessages = this.processMessages(messages, options?.system);
        // When thinking is enabled, temperature MUST be 1 (Claude API requirement)
        const effectiveTemperature = options?.thinking ? 1 : (options?.temperature ?? 1.0);
        // Use model-aware max output tokens: explicit override → model max → 16384 fallback
        const resolvedMaxTokens = options?.max_tokens || getMaxOutputTokens(model);
        const requestData = {
            model: model,
            messages: processedMessages,
            max_tokens: resolvedMaxTokens,
            temperature: effectiveTemperature,
            stream: false, // Explicitly disable streaming for complete() method
        };
        if (options?.tools && options.tools.length > 0) {
            requestData.tools = this.toolsToOpenAIFormat(options.tools);
            if (options.tool_choice) {
                requestData.tool_choice = options.tool_choice;
            }
        }
        this.mergeExtraBodyIntoRequest(requestData, options?.extra_body);
        // Keep core completion fields canonical even if extra_body includes colliding keys.
        requestData.model = model;
        requestData.messages = processedMessages;
        requestData.max_tokens = resolvedMaxTokens;
        requestData.temperature = effectiveTemperature;
        requestData.stream = false;
        // Auto-inject 1M context beta header for Claude Opus 4.6 / Sonnet 4.5
        const mergedHeaders = {};
        if (needsAnthropicContextBetaHeader(model)) {
            mergedHeaders["anthropic-beta"] = ANTHROPIC_1M_BETA_HEADER;
        }
        if (options?.extra_headers) {
            Object.assign(mergedHeaders, options.extra_headers);
        }
        if (Object.keys(mergedHeaders).length > 0) {
            requestData.extra_headers = mergedHeaders;
        }
        // Add Claude extended thinking support
        if (options?.thinking) {
            requestData.thinking = options.thinking;
        }
        // Log request summary for debugging
        console.log('🔷 [KeywordsAdapter] Request:', {
            model: requestData.model,
            message_count: requestData.messages?.length,
            has_tools: !!requestData.tools,
            tool_names: requestData.tools?.map((t) => t.function?.name),
            customer_identifier: requestData.customer_identifier,
            group_identifier: requestData.group_identifier,
            metadata: requestData.metadata,
        });
        const controller = new AbortController();
        // Non-streaming requests get longer timeout (5 minutes)
        const timeoutId = setTimeout(() => controller.abort(), NON_STREAMING_TIMEOUT_MS);
        try {
            const response = await fetch(`${this.baseUrl}/chat/completions`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${apiKey}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(requestData),
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            // Handle 403 Forbidden - likely expired key, force refresh and retry
            if (response.status === 403) {
                console.warn('[KeywordsAdapter] Got 403 Forbidden, refreshing key and retrying');
                const freshApiKey = await this.forceRefreshApiKey();
                const retryResponse = await fetch(`${this.baseUrl}/chat/completions`, {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${freshApiKey}`,
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(requestData),
                    signal: controller.signal
                });
                if (!retryResponse.ok) {
                    const errorDetail = await retryResponse.json().catch(() => ({}));
                    throw new Error(`Keywords API error ${retryResponse.status}: ${JSON.stringify(errorDetail)}`);
                }
                const result = await retryResponse.json();
                const validation = validateLLMResponse(result);
                if (validation.success === false) {
                    throw new Error(`Invalid response format: ${validation.error}`);
                }
                return validation.data;
            }
            if (response.status === 429) {
                console.warn("[KeywordsAdapter] Rate limit hit, waiting before retry");
                await new Promise(resolve => setTimeout(resolve, 2000));
                const retryResponse = await fetch(`${this.baseUrl}/chat/completions`, {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${apiKey}`,
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(requestData),
                    signal: controller.signal
                });
                if (!retryResponse.ok) {
                    const errorDetail = await retryResponse.json().catch(() => ({}));
                    throw new Error(`Keywords API error ${retryResponse.status}: ${JSON.stringify(errorDetail)}`);
                }
                const result = await retryResponse.json();
                const validation = validateLLMResponse(result);
                if (validation.success === false) {
                    throw new Error(`Invalid response format: ${validation.error}`);
                }
                const validatedData = validation.data;
                // TODO: Re-enable when usage increment endpoint is available
                // try {
                //     if (this.jwtToken) {
                //         await this.authManager.incrementUsage(this.jwtToken, 1);
                //     }
                // } catch (e) {
                //     console.warn(`Failed to increment usage: ${e}`);
                // }
                return validatedData;
            }
            if (!response.ok) {
                const errorDetail = await response.json().catch(() => ({}));
                throw new Error(`Keywords API error ${response.status}: ${JSON.stringify(errorDetail)}`);
            }
            const result = await response.json();
            const validation = validateLLMResponse(result);
            if (validation.success === false) {
                throw new Error(`Invalid response format: ${validation.error}`);
            }
            // TODO: Re-enable when usage increment endpoint is available
            // try {
            //     if (this.jwtToken) {
            //         await this.authManager.incrementUsage(this.jwtToken, 1);
            //     }
            // } catch (e) {
            //     console.warn(`Failed to increment usage: ${e}`);
            // }
            return validation.data;
        }
        catch (error) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
                throw new Error(`Non-streaming request timeout after ${NON_STREAMING_TIMEOUT_MS}ms`);
            }
            throw new Error(`Keywords API error: ${error.message || String(error)}`);
        }
    }
    async *stream(model, messages, options) {
        const apiKey = await this.ensureApiKey();
        const processedMessages = this.processMessages(messages, options?.system);
        // When thinking is enabled, temperature MUST be 1 (Claude API requirement)
        const effectiveTemperature = options?.thinking ? 1 : (options?.temperature ?? 1.0);
        // Use model-aware max output tokens: explicit override → model max → 16384 fallback
        const resolvedMaxTokens = options?.max_tokens || getMaxOutputTokens(model);
        const requestData = {
            model: model,
            messages: processedMessages,
            max_tokens: resolvedMaxTokens,
            temperature: effectiveTemperature,
            stream: true
        };
        if (options?.tools && options.tools.length > 0) {
            requestData.tools = this.toolsToOpenAIFormat(options.tools);
            console.log('[KeywordsAdapter] 🔧 Sending tools to API:', requestData.tools.map((t) => t.function?.name));
            if (options.tool_choice) {
                requestData.tool_choice = options.tool_choice;
                console.log('[KeywordsAdapter] Tool choice:', options.tool_choice);
            }
        }
        this.mergeExtraBodyIntoRequest(requestData, options?.extra_body);
        // Keep core streaming fields canonical even if extra_body includes colliding keys.
        requestData.model = model;
        requestData.messages = processedMessages;
        requestData.max_tokens = resolvedMaxTokens;
        requestData.temperature = effectiveTemperature;
        requestData.stream = true;
        // Provider-specific fine-grained tool streaming flags (assumes gateway passthrough).
        this.applyProviderFineGrainedToolStreamingFlags(requestData, model);
        // Auto-inject 1M context beta header for Claude Opus 4.6 / Sonnet 4.5
        const mergedHeaders = {};
        if (needsAnthropicContextBetaHeader(model)) {
            mergedHeaders["anthropic-beta"] = ANTHROPIC_1M_BETA_HEADER;
        }
        if (options?.extra_headers) {
            Object.assign(mergedHeaders, options.extra_headers);
        }
        if (Object.keys(mergedHeaders).length > 0) {
            requestData.extra_headers = mergedHeaders;
        }
        // Add Claude extended thinking support
        if (options?.thinking) {
            requestData.thinking = options.thinking;
        }
        // Log streaming request summary for debugging
        console.log('🔷 [KeywordsAdapter] Streaming Request:', {
            model: requestData.model,
            message_count: requestData.messages?.length,
            has_tools: !!requestData.tools,
            tool_names: requestData.tools?.map((t) => t.function?.name),
            eager_input_streaming: requestData.tools?.some((t) => t?.eager_input_streaming === true) || false,
            stream_function_call_arguments: requestData.toolConfig?.functionCallingConfig?.streamFunctionCallArguments === true,
            customer_identifier: requestData.customer_identifier,
            group_identifier: requestData.group_identifier,
            metadata: requestData.metadata,
        });
        const controller = new AbortController();
        // Use longer heartbeat when tools are present - LLM can pause 30s+ generating big params
        const hasTools = options?.tools && options.tools.length > 0;
        const heartbeatTimeoutMs = hasTools ? TOOL_CALL_HEARTBEAT_TIMEOUT_MS : CHUNK_HEARTBEAT_TIMEOUT_MS;
        // Start with first-token timeout (60s) - will switch to heartbeat after first chunk
        let currentTimeoutId = setTimeout(() => controller.abort(), FIRST_TOKEN_TIMEOUT_MS);
        let receivedFirstChunk = false;
        // Helper to reset heartbeat timeout on each chunk
        const resetHeartbeat = () => {
            if (currentTimeoutId) {
                clearTimeout(currentTimeoutId);
            }
            currentTimeoutId = setTimeout(() => controller.abort(), heartbeatTimeoutMs);
        };
        try {
            const response = await fetch(`${this.baseUrl}/chat/completions`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${apiKey}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(requestData),
                signal: controller.signal
            });
            // Clear first-token timeout - response received, now waiting for chunks
            if (currentTimeoutId) {
                clearTimeout(currentTimeoutId);
                currentTimeoutId = null;
            }
            // Handle 403 Forbidden in streaming - force refresh and retry
            if (response.status === 403) {
                console.warn('[KeywordsAdapter] Got 403 Forbidden in stream, refreshing key and retrying');
                const freshApiKey = await this.forceRefreshApiKey();
                const retryResponse = await fetch(`${this.baseUrl}/chat/completions`, {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${freshApiKey}`,
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(requestData),
                    signal: controller.signal
                });
                if (!retryResponse.ok) {
                    const errorDetail = await retryResponse.json().catch(() => ({}));
                    throw new Error(`Keywords API error ${retryResponse.status}: ${JSON.stringify(errorDetail)}`);
                }
                // Use the retry response for streaming
                const retryReader = retryResponse.body?.getReader();
                const retryDecoder = new TextDecoder();
                let retryBuffer = "";
                if (!retryReader) {
                    throw new Error("Retry response body is not readable");
                }
                // Start heartbeat for retry stream
                resetHeartbeat();
                try {
                    while (true) {
                        const { done, value } = await retryReader.read();
                        if (done)
                            break;
                        // Reset heartbeat on each chunk
                        if (!receivedFirstChunk) {
                            receivedFirstChunk = true;
                        }
                        resetHeartbeat();
                        retryBuffer += retryDecoder.decode(value, { stream: true });
                        const lines = retryBuffer.split("\n");
                        retryBuffer = lines.pop() || "";
                        for (const line of lines) {
                            const trimmed = line.trim();
                            if (!trimmed || trimmed === "[DONE]")
                                continue;
                            if (trimmed.startsWith("data: ")) {
                                const jsonStr = trimmed.slice(6);
                                try {
                                    const chunk = JSON.parse(jsonStr);
                                    const validation = validateLLMResponse(chunk);
                                    if (validation.success === true) {
                                        yield validation.data;
                                    }
                                    else {
                                        console.warn(`Invalid chunk format: ${validation.error}`);
                                        continue;
                                    }
                                }
                                catch (e) {
                                    console.warn(`Failed to parse SSE chunk: ${jsonStr}`, e);
                                }
                            }
                        }
                    }
                }
                finally {
                    retryReader.releaseLock();
                    // Clear timeout on completion
                    if (currentTimeoutId) {
                        clearTimeout(currentTimeoutId);
                        currentTimeoutId = null;
                    }
                }
                return;
            }
            if (!response.ok) {
                const errorDetail = await response.json().catch(() => ({}));
                throw new Error(`Keywords API error ${response.status}: ${JSON.stringify(errorDetail)}`);
            }
            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            if (!reader) {
                throw new Error("Response body is not readable");
            }
            try {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done)
                        break;
                    // Got data - reset heartbeat timeout
                    if (!receivedFirstChunk) {
                        receivedFirstChunk = true;
                        console.log('[KeywordsAdapter] First chunk received, switching to heartbeat timeout');
                    }
                    resetHeartbeat();
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split("\n");
                    buffer = lines.pop() || "";
                    for (const line of lines) {
                        const trimmed = line.trim();
                        if (!trimmed || trimmed === "[DONE]")
                            continue;
                        if (trimmed.startsWith("data: ")) {
                            const jsonStr = trimmed.slice(6);
                            try {
                                const chunk = JSON.parse(jsonStr);
                                const validation = validateLLMResponse(chunk);
                                if (validation.success === true) {
                                    yield validation.data;
                                }
                                else {
                                    console.warn(`Invalid chunk format: ${validation.error}`);
                                    continue;
                                }
                            }
                            catch (e) {
                                console.warn(`Failed to parse SSE chunk: ${jsonStr}`, e);
                            }
                        }
                    }
                }
            }
            finally {
                reader.releaseLock();
                // Clear any remaining timeout
                if (currentTimeoutId) {
                    clearTimeout(currentTimeoutId);
                    currentTimeoutId = null;
                }
            }
            // TODO: Re-enable when usage increment endpoint is available
            // try {
            //     if (this.jwtToken) {
            //         await this.authManager.incrementUsage(this.jwtToken, 1);
            //     }
            // } catch (e) {
            //     console.warn(`Failed to increment usage: ${e}`);
            // }
        }
        catch (error) {
            // Clean up any remaining timeout
            if (currentTimeoutId) {
                clearTimeout(currentTimeoutId);
                currentTimeoutId = null;
            }
            if (error.name === 'AbortError') {
                const timeoutType = receivedFirstChunk ? 'heartbeat' : 'first-token';
                const timeoutValue = receivedFirstChunk ? heartbeatTimeoutMs : FIRST_TOKEN_TIMEOUT_MS;
                throw new Error(`Streaming ${timeoutType} timeout after ${timeoutValue}ms`);
            }
            yield {
                id: `error-${model}`,
                object: "chat.completion.chunk",
                created: Math.floor(Date.now() / 1000),
                model: model,
                choices: [{
                        index: 0,
                        delta: { content: `Error: ${error.message || String(error)}` },
                        finish_reason: "error"
                    }]
            };
        }
    }
}
